import { useMutation } from '@apollo/client';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { ACCEPT_INVITATION_MUTATION } from './auth.mutations';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from './AuthProvider';
import Logo from '../../Treston_logo.svg';

interface AcceptInvitationFormData {
  password: string;
  confirmPassword: string;
}

interface AcceptInvitationFormProps {
  token: string;
  onSwitchToLogin: () => void;
}

const AcceptInvitationForm: React.FC<AcceptInvitationFormProps> = ({ token, onSwitchToLogin }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [acceptInvitation] = useMutation(ACCEPT_INVITATION_MUTATION);

  const validatePassword = (pass: string) => {
    const errors = [];
    if (pass.length < 8) errors.push(t('auth.passwordMinLength'));
    if (!/[A-Z]/.test(pass)) errors.push(t('auth.passwordUppercase'));
    if (!/[a-z]/.test(pass)) errors.push(t('auth.passwordLowercase'));
    if (!/[0-9]/.test(pass)) errors.push(t('auth.passwordNumber'));
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\?]/.test(pass)) errors.push(t('auth.passwordSpecial'));
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join(', '));
      setIsLoading(false);
      return;
    }

    try {
      const result = await acceptInvitation({
        variables: {
          token,
          password,
        },
      });

      if (result.data?.acceptInvitation) {
        const { user, accessToken, refreshToken } = result.data.acceptInvitation;

        // Store tokens in localStorage
        localStorage.setItem(TOKEN_KEY, accessToken);
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }

        // Redirect to dashboard - the page will reload and AuthProvider will pick up the tokens
        window.location.href = '/panels';
      }
    } catch (err: any) {
      setError(err.message || t('auth.invitationAcceptError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
              </div>
              <h2 className="text-3xl font-semibold text-primary-grey-default">
                {t('auth.invalidInvitationLink')}
              </h2>
              <p className="mt-2 text-sm text-primary-grey-300">
                {t('auth.invalidInvitationLinkInstructions')}
              </p>
              <div className="mt-6">
                <button
                  onClick={onSwitchToLogin}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-orange-default hover:bg-secondary-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-orange-default transition-colors"
                >
                  {t('auth.goToLogin')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
            </div>
            <h2 className="text-3xl font-semibold text-primary-grey-default">
              {t('auth.acceptInvitation')}
            </h2>
            <p className="mt-2 text-sm text-primary-grey-300">
              {t('auth.acceptInvitationInstructions')}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-primary-grey-default mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-primary-grey-default focus:outline-none focus:ring-2 focus:ring-primary-red-default focus:border-primary-red-default sm:text-sm"
                    placeholder={t('auth.password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary-grey-300 hover:text-primary-grey-default focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-grey-default mb-2">
                  {t('auth.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-primary-grey-default focus:outline-none focus:ring-2 focus:ring-primary-red-default focus:border-primary-red-default sm:text-sm"
                    placeholder={t('auth.confirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary-grey-300 hover:text-primary-grey-default focus:outline-none"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="text-red-800 text-sm text-center">{error}</div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-orange-default hover:bg-secondary-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-orange-default disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <Oval
                    color="#ffffff"
                    height={20}
                    width={20}
                    visible={true}
                  />
                ) : (
                  t('auth.completeSetup')
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="font-medium text-primary-red-default hover:text-primary-red-600 focus:outline-none focus:underline transition-colors"
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationForm;